import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { capitalizeWords } from "@/functions/capitalizeFirstLetters";
import { convertPlayerPrice } from "@/functions/convertPlayerPrice";
import { chemStyles } from "@/functions/getChemStyles";
import { MetaRatingIcon } from "@/icons";
import { cn } from "@/lib/utils";
import {
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
  tierSelector,
} from "@/redux/players/playersSlice";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import { BREAKPOINTS } from "@/settings";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

interface CurrentStateProps {
  metaRating: number;
  metaRatingZeroChem: number;
  chemStyleId: number;
  playerRoleId: number;
  rolesPlus: number[];
  price: Price;
}

const SectionElement = ({
  children,
  label,
  hidden = false,
}: {
  children: React.ReactNode;
  label: string;
  hidden?: boolean;
}) => (
  <div
    className={cn(
      "mobile:w-[20%] h-full flex flex-col gap-y-2 items-center justify-center",
      hidden && "hidden",
    )}
  >
    {children}
    <p className="text-xs text-dark-tertiary  hidden mobile:block whitespace-nowrap">
      {label}
    </p>
  </div>
);

interface Price {
  price: number;
  isObjectivePlayer: boolean;
  isSbcPlayer: boolean;
}

export const CurrentState = ({
  metaRating,
  metaRatingZeroChem,
  chemStyleId,
  playerRoleId,
  rolesPlus,
  price,
}: CurrentStateProps) => {
  const rolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
    playerRoleId,
    rolesPlus,
  });
  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({ playerRoleId: playerRoleId }),
  );
  const tier = useSelector(tierSelector(metaRatingZeroChem, playerRoleId));
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const PriceSectionElement = ({ hidden = false }: { hidden?: boolean }) => (
    <SectionElement
      label={
        price.isObjectivePlayer
          ? "Objective"
          : price.isSbcPlayer
            ? "SBC Price"
            : "Market Price"
      }
      hidden={hidden}
    >
      <div className="text-sm text-dark-primary flex flex-row items-center gap-1 max-mobile:w-16">
        <img
          className={cn(
            "w-4 h-4",
            price.isSbcPlayer && "w-4.5 h-4.5",
            price.isObjectivePlayer && "w-5 h-5",
          )}
          src={
            import.meta.env.VITE_APP_ASSETS_BASE_URL +
            "/fc25/misc/" +
            (price.isSbcPlayer
              ? "sbc.png"
              : price.isObjectivePlayer
                ? "xp_not_cached.png"
                : "coin.png")
          }
          alt="ut-coin"
        />
        {price?.price && (
          <p className="text-sm text-dark-primary">
            {isMobile
              ? convertPlayerPrice(price?.price, true)
              : price?.price?.toLocaleString()}
          </p>
        )}
      </div>
    </SectionElement>
  );

  const MobileWrapper = ({ children }: { children: React.ReactNode }) =>
    isMobile ? (
      <div className="flex flex-row w-full justify-center gap-x-4">
        {children}
      </div>
    ) : (
      children
    );

  return (
    <div className="col-span-3 h-18 mobile:h-22.5 my-auto px-2 w-full rounded-xl mobile:rounded-2xl flex flex-col gap-y-2 items-center justify-center mobile:flex-row text-white backdrop-blur-md shadow-soft-dark-bottom bg-panel-background max-mobile:max-w-72 max-mobile:ml-6">
      <MobileWrapper>
        <SectionElement label="Meta Rating">
          <div className="flex flex-row items-center gap-2 text-dark-tertiary max-mobile:w-16 text-center justify-end">
            <p className="mobile:text-xl font-bold text-dark-primary">
              {metaRating?.toFixed(1)}
            </p>
            <MetaRatingIcon
              width={isMobile ? "16" : "20"}
              height={isMobile ? "16" : "20"}
            />
          </div>
        </SectionElement>
        <SectionElement label="Meta Tier">
          <p
            className={cn(
              "mobile:text-lg font-bold max-mobile:w-8 text-center",
              tier?.tierTextColor,
              !playerRoleId && "invisible",
            )}
          >
            {tier?.tierName || "-"}
          </p>
        </SectionElement>
        <PriceSectionElement hidden={!isMobile} />
      </MobileWrapper>
      <MobileWrapper>
        <SectionElement label="Player Role">
          <div
            className={cn(
              "flex flex-row items-center gap-1 mobile:gap-2 bg-white/5 rounded-sm mobile:rounded-full px-1 mobile:pl-2 mobile:pr-3 py-1 h-6 mobile:h-8",
              !playerRoleId && "invisible",
            )}
          >
            <p className="text-tiny mobile:text-sm text-dark-secondary truncate">
              {playerRoleLabel}
            </p>
            {rolesPlusOrPlusPlus && (
              <PlusPlus
                plus={rolesPlusOrPlusPlus === "plus"}
                plusPlus={rolesPlusOrPlusPlus === "plusPlus"}
              />
            )}
          </div>
        </SectionElement>
        <SectionElement label={"Chemstyle"}>
          <div
            className={cn(
              "flex flex-row items-center gap-2 bg-white/5 rounded-sm mobile:rounded-full pl-2 pr-3 py-1 h-6 mobile:h-8",
              !chemStyleId && "invisible",
            )}
          >
            <div
              className="mobile:text-xl text-dark-primary w-6"
              style={{
                fontFamily: "UltimateTeamIcons",
              }}
            >
              {chemStylesToIcon[chemStyleId]}
            </div>
            <p className="text-tiny mobile:text-sm text-dark-secondary -ml-1.5">
              {capitalizeWords(chemStyles[chemStyleId])}
            </p>
          </div>
        </SectionElement>
        <PriceSectionElement hidden={isMobile} />
      </MobileWrapper>
    </div>
  );
};
